import { useEffect, useState } from "react";
import { Bot, Eye, Loader2, Pencil, Send, Sparkles, Trash2, TestTube2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

const RUBRIQUES = ["Actualités apicoles", "Santé & bien-être", "Recettes au miel", "Cocktails au miel (avec & sans alcool)"];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminGazetteAgent() {
  const [drafts, setDrafts] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState("");
  const [preview, setPreview] = useState(null); // { draft, html }
  const [editing, setEditing] = useState(null); // draft
  const [busyId, setBusyId] = useState("");

  const loadDrafts = () =>
    api.get("/admin/gazette-agent/drafts").then(({ data }) => setDrafts(data)).catch(() => setDrafts([]));

  useEffect(() => { loadDrafts(); }, []);

  const pollJob = (jobId) =>
    new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = setInterval(async () => {
        try {
          const { data } = await api.get(`/admin/gazette-agent/jobs/${jobId}`);
          if (data.status === "done") { clearInterval(timer); resolve(data); }
          else if (data.status === "error") { clearInterval(timer); reject(new Error(data.message || "Échec")); }
          else if (Date.now() - started > 240000) { clearInterval(timer); reject(new Error("Délai dépassé")); }
        } catch (e) { clearInterval(timer); reject(e); }
      }, 3000);
    });

  const handleRun = async () => {
    setGenerating(true);
    setGenMsg("L'agent rédige les articles… (cela peut prendre 1 à 2 minutes)");
    try {
      const { data } = await api.post("/admin/gazette-agent/run", {});
      const job = await pollJob(data.job_id);
      toast.success(`Nouveau numéro généré — ${job.message}`);
      setGenMsg("");
      loadDrafts();
    } catch (err) {
      toast.error(err.message || formatApiErrorDetail(err.response?.data?.detail));
      setGenMsg("");
    } finally {
      setGenerating(false);
    }
  };

  const handlePreview = async (draft) => {
    setBusyId(draft.draft_id);
    try {
      const { data } = await api.get(`/admin/gazette-agent/drafts/${draft.draft_id}`, { params: { origin_url: window.location.origin } });
      setPreview({ draft, html: data.preview_html });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const handleTest = async (draft) => {
    setBusyId(draft.draft_id);
    try {
      const { data } = await api.post(`/admin/gazette-agent/drafts/${draft.draft_id}/test`, { origin_url: window.location.origin });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const handleSend = async (draft) => {
    if (!window.confirm(`Envoyer « ${draft.subject} » à tous les abonnés actifs ?`)) return;
    setBusyId(draft.draft_id);
    try {
      const { data } = await api.post(`/admin/gazette-agent/drafts/${draft.draft_id}/send`, { origin_url: window.location.origin });
      toast.success(data.message);
      loadDrafts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (draft) => {
    if (!window.confirm(`Supprimer le brouillon « ${draft.subject} » ? (les articles de la Gazette ne sont pas supprimés)`)) return;
    setBusyId(draft.draft_id);
    try {
      await api.delete(`/admin/gazette-agent/drafts/${draft.draft_id}`);
      toast.success("Brouillon supprimé");
      loadDrafts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const saveEdit = async () => {
    setBusyId(editing.draft_id);
    try {
      await api.put(`/admin/gazette-agent/drafts/${editing.draft_id}`, { subject: editing.subject, intro: editing.intro });
      toast.success("Brouillon mis à jour");
      setEditing(null);
      loadDrafts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  return (
    <div data-testid="nl-panel-agent" className="space-y-6">
      {/* Encart agent */}
      <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
              <Bot size={20} className="text-honey" /> Agent hebdomadaire de la Gazette
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-bark-soft">
              Chaque <strong>lundi à 08h00</strong>, l'agent rédige automatiquement un nouveau numéro en 4 rubriques
              (Actualités apicoles, Santé &amp; bien-être, Recettes au miel, Cocktails au miel). Les articles arrivent
              en <strong>brouillon</strong> dans la Gazette, et une newsletter est préparée ici, prête à relire puis envoyer.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {RUBRIQUES.map((r) => (
                <span key={r} className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-deep">{r}</span>
              ))}
            </div>
          </div>
          <button
            data-testid="agent-run-now"
            onClick={handleRun}
            disabled={generating}
            className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:opacity-60"
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {generating ? "Génération…" : "Lancer maintenant"}
          </button>
        </div>
        {genMsg && <p data-testid="agent-gen-msg" className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-deep">{genMsg}</p>}
      </div>

      {/* Liste des brouillons */}
      <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
        <h3 className="mb-4 font-serif text-lg font-semibold text-bark">Numéros préparés</h3>
        {!drafts ? (
          <p className="text-sm text-bark-soft">Chargement…</p>
        ) : drafts.length === 0 ? (
          <p data-testid="agent-drafts-empty" className="text-sm text-bark-soft">
            Aucun numéro pour le moment. Cliquez sur « Lancer maintenant » pour en générer un.
          </p>
        ) : (
          <ul className="divide-y divide-amber-900/10">
            {drafts.map((d) => (
              <li key={d.draft_id} data-testid={`agent-draft-${d.draft_id}`} className="flex flex-wrap items-center justify-between gap-3 py-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-bark">{d.subject}</p>
                    {d.status === "sent" ? (
                      <span className="rounded-full bg-sage-light px-2.5 py-0.5 text-xs font-medium text-sage">Envoyée</span>
                    ) : (
                      <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-deep">Brouillon</span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-stone-mute">
                    {(d.sections?.length || 0)} article(s) · généré le {fmtDate(d.created_at)}
                    {d.status === "sent" && d.sent_count != null ? ` · ${d.sent_count} envoi(s)` : ""}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <button data-testid={`agent-preview-${d.draft_id}`} onClick={() => handlePreview(d)} disabled={busyId === d.draft_id}
                    className="flex items-center gap-1.5 rounded-full border border-amber-800/20 bg-white px-3.5 py-2 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-50">
                    <Eye size={14} /> Aperçu
                  </button>
                  <button data-testid={`agent-edit-${d.draft_id}`} onClick={() => setEditing({ ...d })}
                    className="flex items-center gap-1.5 rounded-full border border-amber-800/20 bg-white px-3.5 py-2 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">
                    <Pencil size={14} /> Modifier
                  </button>
                  <button data-testid={`agent-test-${d.draft_id}`} onClick={() => handleTest(d)} disabled={busyId === d.draft_id}
                    className="flex items-center gap-1.5 rounded-full border border-sage/40 bg-white px-3.5 py-2 text-xs font-medium text-sage transition-colors hover:bg-sage-light disabled:opacity-50">
                    <TestTube2 size={14} /> Test (à moi)
                  </button>
                  <button data-testid={`agent-send-${d.draft_id}`} onClick={() => handleSend(d)} disabled={busyId === d.draft_id}
                    className="flex items-center gap-1.5 rounded-full bg-amber-deep px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-honey disabled:opacity-50">
                    <Send size={14} /> Envoyer aux abonnés
                  </button>
                  <button data-testid={`agent-delete-${d.draft_id}`} onClick={() => handleDelete(d)} disabled={busyId === d.draft_id}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10 disabled:opacity-50" aria-label="Supprimer">
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal aperçu */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setPreview(null)}>
          <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-amber-900/10 px-5 py-3">
              <p className="font-serif text-lg font-semibold text-bark">Aperçu de la newsletter</p>
              <button data-testid="agent-preview-close" onClick={() => setPreview(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-bark-soft hover:bg-sand"><X size={18} /></button>
            </div>
            <iframe title="Aperçu newsletter" srcDoc={preview.html} className="h-[70vh] w-full border-0" />
          </div>
        </div>
      )}

      {/* Modal édition */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <p className="font-serif text-lg font-semibold text-bark">Modifier le numéro</p>
              <button onClick={() => setEditing(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-bark-soft hover:bg-sand"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-bark">Objet / titre du numéro</label>
                <input data-testid="agent-edit-subject" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-bark">Introduction</label>
                <textarea data-testid="agent-edit-intro" rows={4} value={editing.intro || ""} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} className={`${inputClass} resize-y`} />
              </div>
              <p className="text-xs text-stone-mute">Le contenu des articles se modifie dans l'onglet Gazette.</p>
              <button data-testid="agent-edit-save" onClick={saveEdit} disabled={busyId === editing.draft_id}
                className="flex items-center gap-2 rounded-full bg-amber-deep px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-honey disabled:opacity-60">
                {busyId === editing.draft_id ? <Loader2 size={15} className="animate-spin" /> : null} Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
